import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import ScorecardNavigation from '../ScorecardNavigation';
import FullPageSpinner from '../../FullPageSpinner';
import {
  WINDOW_OPTIONS,
  DEFAULT_WINDOW,
  WindowDays,
  LocationOption,
  prettyType,
  formatClock,
} from './interactionEvaluationsQueries';
import { DisagreementChips, verdictClass } from './InteractionTypeReview';
import {
  getInteractionDisagreements,
  getInteractionEvalLocations,
  DisagreementRow,
  ReviewJudgement,
} from '../../../services/scopedReadsService';

// feat-160 (ADR 0001) — standalone cross-type disagreements view. Lists the
// annotator↔offline, annotator↔live, and human↔offline disagreements ACROSS the
// judged types (greeting + upsell) from GET /admin/interaction-evaluations/
// disagreements. Compact rows (no transcript) — each links into the per-type
// review card (?focus=<id>) for the full transcript + verify controls. Admin-only,
// cross-tenant, same Window/Location semantics as the eval family. Verdicts +
// disagreement tokens are rendered VERBATIM (feedback_metrics_single_source).

const PAGE_SIZE = 25; // matches the review endpoint's server default; page when count == limit

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  try {
    const utc = value.endsWith('Z') ? value : `${value}Z`;
    return formatInTimeZone(parseISO(utc), 'America/Los_Angeles', 'MMM d, yyyy h:mm a');
  } catch {
    return value;
  }
};

const parseWindow = (raw: string | null): WindowDays => {
  const n = Number(raw);
  return (WINDOW_OPTIONS as readonly number[]).includes(n) ? (n as WindowDays) : DEFAULT_WINDOW;
};

// Small verdict pill — verbatim verdict text, colored by verdictClass. '—' when
// the row has no such judgement.
const VerdictPill: React.FC<{ j?: ReviewJudgement | null }> = ({ j }) => {
  if (!j) return <span className="text-inkMuted">—</span>;
  // feat-174 — `uncertain` color flows through the shared verdictClass. This compact
  // view links into the per-type review card for full detail; here we fold the
  // judge's rubric (AC4) into the hover tooltip alongside the reasoning so the
  // "which criteria did the judge grade on" signal is recoverable without leaving.
  const title =
    [
      j.reasoning ?? null,
      j.rubric_criteria && j.rubric_criteria.length > 0
        ? `Judge rubric: ${j.rubric_criteria.map((c) => c.key).join(', ')}`
        : null,
    ]
      .filter(Boolean)
      .join('\n\n') || undefined;
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${verdictClass(j.verdict)}`}
      title={title}
    >
      {j.verdict ?? 'unknown'}
    </span>
  );
};

const reviewHref = (
  row: DisagreementRow,
  windowDays: WindowDays,
  locationId: string,
  liveOnly: boolean,
): string => {
  const params = new URLSearchParams();
  params.set('days', String(windowDays));
  if (locationId) params.set('location_id', locationId);
  // feat-196 (AC10) — the scope follows the reviewer one more hop in. Canonical
  // default-ON-by-absence: only ever `live_only=false`, never `=true`.
  if (!liveOnly) params.set('live_only', 'false');
  // feat-186 — the binary `disagreements_only` param is GONE (AC6). Its intent
  // ("land in a discrepancy-focused review") is now the 100/0/0 composition preset,
  // so this deep-link carries `shares=100-0-0` instead of the retired param. `focus`
  // still pins the specific row within that discrepancy-heavy queue.
  params.set('shares', '100-0-0');
  params.set('focus', row.id);
  return `/restaurant/internal/interaction-evaluations/review/${row.interaction_type}?${params.toString()}`;
};

const InteractionDisagreements: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const windowDays = parseWindow(searchParams.get('days'));
  const locationId = searchParams.get('location_id') || '';
  // feat-196 (AC10) — "Live clients only", carried in from the dashboard link and
  // default ON. Read exactly as feat-188's `exclude_human_reviewed` is
  // (InteractionTypeReview.tsx): default-ON is expressed by ABSENCE, `=false` is the
  // one canonical opt-out, and ANY other literal in a hand-mangled URL resolves to
  // ON — i.e. it fails SAFE toward the live-only population rather than 422-ing the
  // page. Never re-serialized as `live_only=true`, so there is one URL per state.
  const liveOnly = searchParams.get('live_only') !== 'false';

  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [rows, setRows] = useState<DisagreementRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Location options for the filter (loaded once). Non-fatal.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const opts = await getInteractionEvalLocations({ liveOnly });
        if (!cancelled) setLocations(opts as LocationOption[]);
      } catch (e) {
        Sentry.captureException(e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // feat-196 (D3) — the dropdown must not OFFER a non-live client's location.
  }, [liveOnly]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await getInteractionDisagreements({
        days: windowDays,
        locationId: locationId || undefined,
        limit: PAGE_SIZE,
        offset: 0,
        liveOnly,
      });
      setRows(resp.items);
      setHasMore(resp.count >= PAGE_SIZE);
    } catch (e) {
      console.error('Failed to load disagreements:', e);
      Sentry.captureException(e);
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [windowDays, locationId, liveOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = useCallback(async () => {
    setIsLoadingMore(true);
    try {
      const resp = await getInteractionDisagreements({
        days: windowDays,
        locationId: locationId || undefined,
        limit: PAGE_SIZE,
        offset: rows.length,
        liveOnly,
      });
      setRows((prev) => [...prev, ...resp.items]);
      setHasMore(resp.count >= PAGE_SIZE);
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setIsLoadingMore(false);
    }
  }, [windowDays, locationId, rows.length, liveOnly]);

  const setWindow = (d: WindowDays) => {
    const next = new URLSearchParams(searchParams);
    next.set('days', String(d));
    setSearchParams(next, { replace: true });
  };
  const setLocation = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('location_id', id);
    else next.delete('location_id');
    setSearchParams(next, { replace: true });
  };
  // feat-196 — turning the filter back ON DELETES the key rather than writing
  // `=true`, so a bare URL and a toggled-ON URL are byte-identical and there is
  // exactly one canonical URL per state (ADR 0001 §4.3, mirroring feat-188).
  const setLiveOnly = (on: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (on) next.delete('live_only');
    else next.set('live_only', 'false');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="mb-6">
          <Link
            to="/restaurant/internal/interaction-evaluations"
            className="text-sm text-coral hover:underline"
          >
            ← Back to Interaction Evaluations
          </Link>
          <h1 className="text-4xl font-display font-semibold text-ink mb-1 mt-2">Disagreements</h1>
          <p className="text-sm text-inkMuted">
            Where the annotator, its live double-check, the independent offline judge, and your human
            verification disagree — across the judged types (greeting + upsell). Open a row to review
            the full transcript and verdict. Cross-tenant admin review.
          </p>
        </div>

        {/* Controls: Window + Location + Live clients only (mirror the eval table) */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-ink">Window:</span>
            <div className="inline-flex rounded-lg border border-softBorder bg-white overflow-hidden">
              {WINDOW_OPTIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setWindow(d)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    windowDays === d ? 'bg-coral text-white' : 'text-inkMuted hover:bg-cream'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="disagreementLocationFilter" className="text-sm font-medium text-ink">
              Location:
            </label>
            <select
              id="disagreementLocationFilter"
              value={locationId}
              onChange={(e) => setLocation(e.target.value)}
              className="px-3 py-1.5 border border-softBorder rounded-md bg-white text-sm"
            >
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>

          {/* feat-196 (AC10) — the toggle carried in from the dashboard, rendered so
              its state is VISIBLE and reversible here rather than only inherited.
              A filter that silently persists across a navigation but can't be seen
              or changed on the page it lands on is indistinguishable from a bug. */}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="live-only-toggle"
                checked={liveOnly}
                onChange={(e) => setLiveOnly(e.target.checked)}
                className="h-4 w-4 rounded border-softBorder text-coral focus:ring-coral"
              />
              <span className="text-sm font-medium text-ink">Live clients only</span>
            </label>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            Error loading data: {error}
          </div>
        )}

        {isLoading ? (
          <FullPageSpinner />
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-inkMuted bg-white border border-softBorder rounded-xl">
            No disagreements in this window — the annotator, judge, and humans agree. 🎉
          </div>
        ) : (
          <>
            <div className="bg-white shadow-sm border border-softBorder rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-softBorder">
                  <thead className="bg-cream/60">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Type</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Server / Location</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">When</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Annotator</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Live</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Offline</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Human</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Disagreement</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-softBorder">
                    {rows.map((r) => {
                      const offline = r.offline;
                      const live = r.live;
                      const grade = r.data?.grade ?? null;
                      const human =
                        r.correct_human_verified === true
                          ? 'Correct'
                          : r.correct_human_verified === false
                            ? 'Incorrect'
                            : '—';
                      return (
                        <tr key={r.id} className="hover:bg-cream/40 align-top">
                          <td className="px-4 py-2.5 text-sm font-medium text-ink whitespace-nowrap">
                            {prettyType(r.interaction_type)}
                          </td>
                          <td className="px-4 py-2.5 text-sm text-ink">
                            {r.server_name || '—'}
                            {r.location_name && (
                              <span className="text-inkMuted"> · {r.location_name}</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-sm text-inkMuted whitespace-nowrap">
                            {formatDateTime(r.created)}
                            <span className="block font-mono text-xs">
                              span {formatClock(r.start_time)}–
                              {r.end_time != null ? formatClock(r.end_time) : '…'}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-sm text-ink whitespace-nowrap">
                            {grade ?? '—'}
                          </td>
                          <td className="px-4 py-2.5 text-sm">
                            <VerdictPill j={live} />
                          </td>
                          <td className="px-4 py-2.5 text-sm">
                            <VerdictPill j={offline} />
                          </td>
                          <td className="px-4 py-2.5 text-sm text-inkMuted whitespace-nowrap">{human}</td>
                          <td className="px-4 py-2.5 text-sm">
                            <DisagreementChips
                              tokens={r.disagreements}
                              correctHumanVerified={r.correct_human_verified}
                            />
                          </td>
                          <td className="px-4 py-2.5 text-sm whitespace-nowrap">
                            <Link
                              to={reviewHref(r, windowDays, locationId, liveOnly)}
                              className="text-coral hover:underline font-medium"
                              title="Open this interaction in the per-type review card"
                            >
                              Open in review →
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {hasMore && (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="px-4 py-2 rounded-md text-sm font-medium border border-softBorder bg-white text-ink hover:bg-cream disabled:opacity-50"
                >
                  {isLoadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InteractionDisagreements;
